import { Badge, Button, Menu, Table, Text } from '@mantine/core';
import { DateTime } from 'luxon';
import React from 'react';
import { IShiftSession } from '../../../types/shifts/Shift';
import { CenteredLoader } from '../../common/loader';

interface ShiftSessionWithName extends IShiftSession {
  shiftName: string;
}

interface ShiftSessionTableProps {
  sessions: ShiftSessionWithName[];
  onViewAppointments: (session: ShiftSessionWithName) => void;
  onPaymentStatusUpdate: (session: ShiftSessionWithName, status: 'unpaid' | 'partial' | 'paid') => void;
  isLoading?: boolean;
}

const getPaymentStatusColor = (status?: string) => {
  switch (status) {
    case 'paid':
      return 'green';
    case 'partial':
      return 'orange';
    case 'unpaid':
    default:
      return 'red';
  }
};

const getPaymentStatusLabel = (status?: string) => {
  switch (status) {
    case 'paid':
      return 'Fully Paid';
    case 'partial':
      return 'Partially Paid';
    case 'unpaid':
    default:
      return 'Unpaid';
  }
};

export const ShiftSessionTable: React.FC<ShiftSessionTableProps> = ({
  sessions,
  onViewAppointments,
  onPaymentStatusUpdate,
  isLoading = false
}) => {
  if (isLoading && sessions.length === 0) {
    return <CenteredLoader style={{ padding: '2rem 0' }} />;
  }

  if (sessions.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No shift sessions found for the selected date range
      </Text>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}
        >
          <CenteredLoader />
        </div>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Shift Name</Table.Th>
          <Table.Th>Start</Table.Th>
          <Table.Th>End</Table.Th>
          <Table.Th>Payment Status</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sessions.map((session) => (
          <Table.Tr key={session._id}>
            <Table.Td>
              <Text fw={500}>{session.shiftName}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">
                {DateTime.fromJSDate(new Date(session.start)).toFormat('dd/MM/yyyy HH:mm')}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">
                {DateTime.fromJSDate(new Date(session.finish)).toFormat('dd/MM/yyyy HH:mm')}
              </Text>
            </Table.Td>
            <Table.Td>
              <Badge
                color={getPaymentStatusColor(session.paymentStatus)}
                variant="filled"
                size="sm"
              >
                {getPaymentStatusLabel(session.paymentStatus)}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Button.Group>
                <Button
                  size="xs"
                  onClick={() => onViewAppointments(session)}
                  disabled={isLoading}
                  variant="outline"
                  color="blue"
                >
                  {session.paymentStatus === 'paid' ? 'View Details' : 'Manage Payment'}
              </Button>
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={isLoading}
                      color='blue'
                      style={{
                        borderLeft: 'none',
                        paddingLeft: '8px',
                        paddingRight: '8px'
                      }}
                    >
                      ▼
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Update Payment Status</Menu.Label>
                    <Menu.Item
                      onClick={() => onPaymentStatusUpdate(session, 'unpaid')}
                      disabled={session.paymentStatus === 'unpaid'}
                      color="red"
                    >
                      Mark as Unpaid
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => onPaymentStatusUpdate(session, 'partial')}
                      disabled={session.paymentStatus === 'partial'}
                      color="orange"
                    >
                      Mark as Partially Paid
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => onPaymentStatusUpdate(session, 'paid')}
                      disabled={session.paymentStatus === 'paid'}
                      color="green"
                    >
                      Mark as Paid
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Button.Group>
            </Table.Td>
          </Table.Tr>
        ))}
        </Table.Tbody>
      </Table>
    </div>
  );
};
